/**
 * Authentication service for mini apps (Telegram, MAX, ...)
 * Handles authentication with backend using platform-specific credentials
 */

import { detectPlatform, getPlatformName } from '../platforms';
import { hasPendingAccountLinkSession } from '../utils/accountLinking';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const TOKEN_REFRESH_THRESHOLD_MINUTES = 5; // Refresh token 5 minutes before expiration
const TOKEN_CHECK_INTERVAL_MS = 60 * 60 * 1000; // Check every 60 minutes (1 hour)

/**
 * Decode JWT token to get expiration time
 * @param {string} token - JWT token
 * @returns {Object|null} - Decoded token payload or null if invalid
 */
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if token is expired or about to expire
 * @param {string} token - JWT token
 * @param {number} thresholdMinutes - Minutes before expiration to consider token as "expiring soon"
 * @returns {Object} - { isExpired: boolean, isExpiringSoon: boolean, expiresAt: number|null }
 */
function checkTokenExpiration(token, thresholdMinutes = TOKEN_REFRESH_THRESHOLD_MINUTES) {
  if (!token) {
    return { isExpired: true, isExpiringSoon: true, expiresAt: null };
  }

  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return { isExpired: true, isExpiringSoon: true, expiresAt: null };
  }

  const expiresAt = decoded.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  const thresholdMs = thresholdMinutes * 60 * 1000;
  const timeUntilExpiry = expiresAt - now;

  return {
    isExpired: timeUntilExpiry <= 0,
    isExpiringSoon: timeUntilExpiry <= thresholdMs,
    expiresAt: expiresAt
  };
}

class AuthService {
  constructor() {
    this.token = null;
    this.user = null;
    this.isAuthenticated = false;
    this.refreshInterval = null;
    this.isRefreshing = false;
    this.isLoggingOut = false; // Предотвращает множественные вызовы logout

    // Load token from localStorage on initialization
    this.loadToken();

    // Start token refresh monitoring
    this.startTokenRefreshMonitoring();
  }

  /**
   * Load token from localStorage
   */
  loadToken() {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      console.log('[Auth] Loading token from localStorage');
      // Check if token is still valid
      const expiration = checkTokenExpiration(storedToken);
      if (expiration.isExpired) {
        // Token is expired, clear it
        console.warn('[Auth] Stored token is expired, clearing it');
        this.clearAuth();
        return;
      }
      this.token = storedToken;
      this.isAuthenticated = true;
      const decoded = decodeJWT(storedToken);
      if (decoded && decoded.exp) {
        const expiresAt = new Date(decoded.exp * 1000);
        const minutesLeft = Math.round((decoded.exp * 1000 - Date.now()) / 1000 / 60);
        console.log(`[Auth] Token loaded successfully. Expires at: ${expiresAt.toLocaleString()}, ${minutesLeft} minutes left`);
      }
    } else {
      console.log('[Auth] No token found in localStorage');
    }
  }

  /**
   * Save token to localStorage
   */
  saveToken(token) {
    this.token = token;
    this.isAuthenticated = true;
    localStorage.setItem('auth_token', token);
    
    const decoded = decodeJWT(token);
    if (decoded && decoded.exp) {
      const expiresAt = new Date(decoded.exp * 1000);
      const minutesLeft = Math.round((decoded.exp * 1000 - Date.now()) / 1000 / 60);
      console.log(`[Auth] Token saved. Expires at: ${expiresAt.toLocaleString()}, ${minutesLeft} minutes left`);
    }
    
    // Restart monitoring with new token
    this.startTokenRefreshMonitoring();
  }

  /**
   * Clear authentication data
   */
  clearAuth() {
    // Предотвращаем множественные вызовы logout
    if (this.isLoggingOut) {
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Auth] Clearing authentication data');
    }
    
    this.user = null;
    this.isAuthenticated = false;
    this.stopTokenRefreshMonitoring();
    
    // Call logout endpoint to clear httpOnly cookie (только один раз)
    this.isLoggingOut = true;
    fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    }).catch(() => {
      // Ignore errors on logout
    }).finally(() => {
      this.isLoggingOut = false;
    });
  }

  /**
   * Get authorization header
   */
  getAuthHeader() {
    if (this.token) {
      return {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json'
      };
    }
    return {
      'Content-Type': 'application/json'
    };
  }

  /**
   * Authenticate using platform-specific credentials
   * @param {string} provider - Platform id (telegram, max, ...)
   * @param {string} payload - Platform auth payload (e.g. Telegram initData)
   */
  async authenticateWithProvider(provider, payload) {
    if (provider === 'telegram') {
      return this.authenticateWithTelegram(payload);
    }
    if (provider === 'max') {
      return this.authenticateWithMax(payload);
    }
    throw new Error(`Unsupported auth provider: ${provider}`);
  }

  /**
   * Authenticate using Telegram initData
   * @param {string} initData - Telegram WebApp initData
   * @returns {Promise<Object>} - Token response
   */
  async authenticateWithTelegram(initData) {
    try {
      console.log('[Auth] Starting authentication with Telegram initData');
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/telegram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ init_data: initData })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[Auth] Authentication failed:', error.detail || 'Authentication failed');
        throw new Error(error.detail || 'Authentication failed');
      }

      const data = await response.json();
      console.log('[Auth] Authentication successful, saving token');
      this.saveToken(data.access_token);
      
      // Fetch user info
      await this.fetchUserInfo();
      
      return data;
    } catch (error) {
      console.error('[Auth] Authentication error:', error);
      throw error;
    }
  }

  /**
   * Authenticate using MAX initData
   * @param {string} initData - MAX WebApp initData
   * @returns {Promise<Object>} - Token response
   */
  async authenticateWithMax(initData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/max`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ init_data: initData }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[Auth] MAX authentication failed:', error.detail || 'Authentication failed');
        throw new Error(error.detail || 'Authentication failed');
      }

      const data = await response.json();
      console.log('[Auth] MAX authentication successful, saving token');
      this.saveToken(data.access_token);

      await this.fetchUserInfo();

      return data;
    } catch (error) {
      console.error('[Auth] MAX authentication error:', error);
      throw error;
    }
  }

  /**
   * Debug login via POST /api/v1/auth/login (requires DEBUG_MODE=1 on backend).
   * @param {string} providerUserId - Telegram or MAX user id from seed/DB
   * @param {string} provider - Platform id (telegram, max)
   */
  async authenticateWithDebugLogin(providerUserId, provider = 'telegram') {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        provider,
        provider_user_id: String(providerUserId),
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const detail = error.detail;
      const message = typeof detail === 'string'
        ? detail
        : 'Debug login failed. Убедитесь, что DEBUG_MODE=1 и пользователь есть в БД.';
      throw new Error(message);
    }

    const data = await response.json();
    this.saveToken(data.access_token);
    await this.fetchUserInfo();
    return data;
  }

  /**
   * Refresh token using platform initData
   * @returns {Promise<boolean>} - True if refresh was successful
   */
  async refreshToken() {
    if (hasPendingAccountLinkSession()) {
      console.log('[Auth] Skipping token refresh while account link is pending');
      return false;
    }

    if (this.isRefreshing) {
      console.log('[Auth] Token refresh already in progress, skipping...');
      return false;
    }

    const platform = detectPlatform();
    if (!platform) {
      console.warn('[Auth] Cannot refresh token: platform WebApp not available');
      return false;
    }

    const payload = platform.getAuthPayload();
    if (!payload) {
      console.warn('[Auth] Cannot refresh token: no auth payload available');
      return false;
    }

    try {
      this.isRefreshing = true;
      console.log(`[Auth] Refreshing token via ${getPlatformName(platform)}...`);
      await this.authenticateWithProvider(getPlatformName(platform), payload);
      console.log('[Auth] ✅ Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('[Auth] Token refresh error:', error);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Check token expiration and refresh if needed
   * @returns {Promise<boolean>} - True if token is valid or was refreshed
   */
  async ensureValidToken() {
    if (hasPendingAccountLinkSession()) {
      console.log('[Auth] Skipping token validation while account link is pending');
      return Boolean(this.token);
    }

    if (!this.token) {
      console.log('[Auth] No token available');
      return false;
    }

    const expiration = checkTokenExpiration(this.token);
    const decoded = decodeJWT(this.token);
    
    if (decoded && decoded.exp) {
      const expiresAt = new Date(decoded.exp * 1000);

      if (expiration.isExpired) {
        // Token is expired, try to refresh
        console.warn(`[Auth] ⚠️ Token expired! (was valid until ${expiresAt.toLocaleString()}), attempting refresh...`);
        const refreshed = await this.refreshToken();
        if (!refreshed) {
          console.error('[Auth] Failed to refresh expired token, clearing auth');
          this.clearAuth();
          return false;
        }
        return true;
      }

      if (expiration.isExpiringSoon) {
        // Token is expiring soon, but don't spam - only refresh if not already refreshing
        if (!this.isRefreshing) {
          await this.refreshToken();
        }
        return true;
      }

      // Token is still valid
      return true;
    }

    console.warn('[Auth] Could not decode token expiration');
    return false;
  }

  /**
   * Start monitoring token expiration and auto-refresh
   */
  startTokenRefreshMonitoring() {
    // Clear any existing interval
    this.stopTokenRefreshMonitoring();

    if (hasPendingAccountLinkSession()) {
      console.log('[Auth] Not starting token monitoring: account link pending');
      return;
    }

    if (!this.isAuthenticated || !this.token) {
      console.log('[Auth] Not starting token monitoring: not authenticated');
      return;
    }

    console.log(`[Auth] 🔄 Starting token refresh monitoring (checking every ${TOKEN_CHECK_INTERVAL_MS / 1000} seconds)`);
    
    // Check token expiration periodically
    this.refreshInterval = setInterval(async () => {
      if (this.isAuthenticated && this.token) {
        console.log('[Auth] 🔍 Periodic token check...');
        await this.ensureValidToken();
      } else {
        console.log('[Auth] Skipping periodic check: not authenticated or no token');
      }
    }, TOKEN_CHECK_INTERVAL_MS);
  }

  /**
   * Stop monitoring token expiration
   */
  stopTokenRefreshMonitoring() {
    if (this.refreshInterval) {
      console.log('[Auth] Stopping token refresh monitoring');
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Fetch current user information
   * @returns {Promise<Object>} - User information
   */
  async fetchUserInfo() {
    try {
      // Ensure token is valid before making request
      console.log('[Auth] Fetching user info, ensuring token is valid...');
      await this.ensureValidToken();

      const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        method: 'GET',
        headers: this.getAuthHeader()
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, try to refresh once
          console.log('[Auth] Got 401 error, attempting token refresh...');
          const refreshed = await this.refreshToken();
          if (refreshed) {
            // Retry the request with new token
            console.log('[Auth] Retrying request with new token...');
            const retryResponse = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
              method: 'GET',
              headers: this.getAuthHeader()
            });
            if (!retryResponse.ok) {
              console.error('[Auth] Retry request failed, clearing auth');
              this.clearAuth();
              throw new Error('Session expired');
            }
            const user = await retryResponse.json();
            this.user = user;
            console.log('[Auth] Request retry successful');
            return user;
          } else {
            console.error('[Auth] Token refresh failed, clearing auth');
            this.clearAuth();
            throw new Error('Session expired');
          }
        }
        throw new Error('Failed to fetch user info');
      }

      const user = await response.json();
      this.user = user;
      return user;
    } catch (error) {
      console.error('Fetch user info error:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  checkAuth() {
    return this.isAuthenticated && this.token !== null;
  }

  /**
   * Get current token
   * @returns {string|null}
   */
  getToken() {
    return this.token;
  }

  /**
   * Create a short-lived link session for cross-platform account linking.
   * @param {string|null} accessToken
   * @returns {Promise<{link_token: string, expires_in: number}>}
   */
  async createLinkSession(accessToken = null) {
    const token = accessToken || this.token;
    if (!token) {
      throw new Error('Требуется авторизация');
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/auth/link-session`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Не удалось начать привязку аккаунта');
    }

    return response.json();
  }

  /**
   * Link another platform identity to the current (or specified) account.
   * @param {string} provider - Target platform (telegram, max)
   * @param {string} initData - Signed init data from that platform
   * @param {{ mergeIfTaken?: boolean, accessToken?: string, linkToken?: string }} options
   */
  async linkIdentity(provider, initData, { mergeIfTaken = true, linkToken = null } = {}) {
    const token = linkToken ? null : this.token;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (!token && !linkToken) {
      throw new Error('Требуется авторизация');
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/auth/link-identity`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        provider,
        init_data: initData,
        merge_if_taken: mergeIfTaken,
        link_token: linkToken || undefined,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const detail = error.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((item) => item.msg || item).join(', ')
          : 'Не удалось привязать аккаунт';
      throw new Error(message);
    }

    const data = await response.json();
    this.user = { type: 'user', user: data.user };

    if (data.access_token) {
      this.saveToken(data.access_token);
    } else if (!hasPendingAccountLinkSession()) {
      await this.fetchUserInfo();
    }

    return data;
  }

  /**
   * Get current user
   * @returns {Object|null}
   */
  getUser() {
    return this.user;
  }
}

// Export singleton instance
export const authService = new AuthService();

// Make authService available globally for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  window.authService = authService;
}

export default authService;

